<template>
  <div class="container">
    <div class="clearfix" style="float:right;">
      <TutorialToggle name="ApplicationStatus" :disabled="tooltipsDisabled" />
    </div>
    <router-link :to="'/dashboard/bidders/' + auctionId" class="alt-link">&lt; Back to Bidders</router-link>

    <div v-if="application.id">

      <div class="clearfix" style="margin: 1rem 0 0;">
        <div class="col12" style="font-weight: 600; text-transform: uppercase; color: #777;">
          Registration Form - {{ auction.address }}
        </div>
        <div class="col12" style="font-weight: 600; color: #777; text-align: right;">
          <span>Reg Form ID #{{ applicationId }}</span>
          <span style="margin-left: 1rem;">Last Updated: {{ application.modified | prettyDate }}</span>
        </div>
      </div>

      <div style="font-family: 'Encode Sans', sans-serif; font-weight: 400; font-size: 24px; margin-bottom: 2em;">
        {{ buyerProfile.firstName }} {{ buyerProfile.lastName }} - {{ buyerProfile.alias }}
      </div>

      <div class="component-container">
        <div class="status-notification" v-if="application.role === auctionRoles.ENTRY.value">
          <div class="color-bar">Buyer still filling in the registration form</div>
        </div>
        <div class="status-notification" v-else-if="application.role === auctionRoles.APPLICANT.value">
          <div class="color-bar">This registration is currently waiting for your approval</div>
        </div>
        <div class="status-notification" v-else-if="application.role === auctionRoles.BUYER.value || application.role === auctionRoles.WINNER.value || application.role === auctionRoles.ABANDON.value">
          <div class="color-bar accepted">This registration has been accepted</div>
        </div>
        <div class="status-notification" v-else-if="application.role === auctionRoles.DISQUALIFIED.value">
          <div class="color-bar rejected">This registration has been rejected</div>
        </div>
        <div class="status-notification" v-else>
          <div class="color-bar rejected">Unknown role: {{ application.role }}</div>
        </div>


        <div class="grey-box-container box" >
          <WithTutorial
            name="ApplicationStatus" :order="1"
            heading="Review Bidder"
            text="Here is the Bidder’s registration form that they are required to complete to participate in your auction."
            offset="30">
            <h3>Personal Information</h3>
          </WithTutorial>

          <div class="clearfix row">
            <div class="col12">
              <h4>First Name</h4>
              <div>{{ buyerProfile.firstName }}</div>
            </div>
            <div class="col12">
              <h4>Last Name</h4>
              <div>{{ buyerProfile.lastName }}</div>
            </div>
          </div>

          <div class="clearfix row">
            <div class="col12">
              <h4>Username</h4>
              <div>{{ buyerProfile.alias }}</div>
            </div>
            <div class="col12">
              <h4>Cell Phone Number</h4>
              <div>{{ buyerProfile.phone }}</div>
            </div>
          </div>

          <div class="clearfix row">
            <div class="col12">
              <h4>Email Address</h4>
              <div>{{ buyerProfile.email }}</div>
            </div>
            <div class="col12">
              <h4>Name of Entity</h4>
              <div>{{ buyerProfile.buyerEntity || '&mdash;' }}</div>
            </div>
          </div>

        </div>

        <div class="grey-box-container box">
          <WithTutorial
            name="ApplicationStatus" :order="2"
            heading="Review Bidder"
            text="You can view their personal information, agency relationship, observers (individuals who will watch them auction) and their required documents."
            offset="30">
            <h3>Agency Relationship</h3>
          </WithTutorial>
          <div class="row">
            <div>{{ agencyRelationshipLabel(application.agencyRelationship) }}</div>
          </div>

          <!-- SELLING_AGENT -->
          <template v-if="application.agencyRelationship === agencyRelationships.SELLING_AGENT.value">
            <div class="clearfix row">
              <div class="col12">
                <h4>Invitation</h4>
                <div v-if="sellingAgentProfile.id">Accepted</div>
                <div v-else>Waiting to Accepted <v-icon color="red darken-4">warning</v-icon></div>
              </div>
            </div>
            <div class="clearfix row">
              <div class="col12">
                <h4>First Name</h4>
                <div>{{ sellingAgentProfile.firstName || '&mdash;' }}</div>
              </div>
              <div class="col12">
                <h4>Last Name</h4>
                <div>{{ sellingAgentProfile.lastName || '&mdash;' }}</div>
              </div>
            </div>
            <div class="clearfix row">
              <div class="col12">
                <h4>Email Address</h4>
                <div>{{ sellingAgentProfile.email || sellingAgentInvitedEmailAddress }}</div>
              </div>
              <div class="col12">
                <h4>Cell Phone Number</h4>
                <div>{{ sellingAgentProfile.phone || '&mdash;' }}</div>
              </div>
            </div>
            <div class="clearfix row">
              <div class="col12">
                <h4>Agent ID</h4>
                <div>{{ sellingAgentProfile.dreNumber || '&mdash;' }}</div>
              </div>
              <div class="col12">
                <h4>Brokerage ID</h4>
                <div>{{ sellingAgentProfile.xxxxx || '&mdash;' }}</div><!-- TODO needs to be added to profile form / special signup additions for selling agent invitations? -->
              </div>
            </div>
            <div class="clearfix row">
              <div class="col12">
                <h4>Company</h4>
                <div>{{ sellingAgentProfile.xxxxx || '&mdash;' }}</div><!-- TODO needs to be added to profile form / special signup additions for selling agent invitations? -->
              </div>
            </div>
          </template>

          <!-- LISTING_AGENT -->
          <template v-else-if="application.agencyRelationship === agencyRelationships.LISTING_AGENT.value">
            <div class="clearfix row">
              <div class="col12">
                <h4>First Name</h4>
                <div>{{ listingAgentProfile.firstName }}</div>
              </div>
              <div class="col12">
                <h4>Last Name</h4>
                <div>{{ listingAgentProfile.lastName }}</div>
              </div>
            </div>
            <div class="clearfix row">
              <div class="col12">
                <h4>Email Address</h4>
                <div>{{ listingAgentProfile.email }}</div>
              </div>
              <div class="col12">
                <h4>Agent ID</h4>
                <div>{{ listingAgentProfile.dreNumber || '&mdash;' }}</div>
              </div>
            </div>
          </template>

          <!-- MYSELF -->
          <template v-else-if="application.agencyRelationship === agencyRelationships.MYSELF.value">
            <div class="clearfix row">
              <div class="col12">
                <h4>Agent ID</h4>
                <div>{{ buyerProfile.dreNumber || '&mdash;' }}</div>
              </div>
            </div>
          </template>

          <!-- unknown relationship -->
          <template v-else>
            <div class="row">
              Unknown or unsupported agency relationship ({{ application.agencyRelationship }}), please contact PFX.
            </div>
          </template>

        </div>

        <div class="grey-box-container box">
          <h3>Required Documents</h3>
          <p>
            Please review the documents that the buyer has uploaded.
            <!--Approve each item by selecting the green check mark.-->
            For missing or inadequate documents, please send comments to the buyer to ask for more details.
          </p>

          <!-- NOTE: this feature was removed (accepting individual buyer requirements)
          <div v-for="req in auction.buyerRequirements" :key="req.id" style="cursor: pointer;" @click="toggleBuyerRequirementStatus(req.id)">
            <v-icon color="green" v-if="isBuyerRequirementStatusTrue(req.id)">check_circle</v-icon>
            <v-icon color="grey lighten-2" v-else>remove_circle</v-icon>
            {{ req.name }}
          </div>
          -->
          <ul class="list-bullets">
            <li v-for="req in auction.buyerRequirements" :key="req.id">
              {{ getLabel(req.docType) }}
            </li>
          </ul>

        </div>

        <div class="grey-box-container box">
          <h3>Uploaded Documents</h3>
          <FileList :auctionId="auction.id" :files="application.applicationFiles" :mutable="false" />
        </div>


        <div class="grey-box-container box">
          <h3>Comments for the Buyer</h3>

          <div class="clearfix row" v-if="application.message">
            <div class="clearfix">
              <img v-if="$store.getters.photoUrl" :src="$store.getters.photoUrl" class="user-avatar" />
              <v-icon medium :dark="dark" v-else class="user-avatar">account_circle</v-icon>
              <div v-html="$options.filters.convertLinebreaks(application.message)" />
            </div>
          </div>
          <div class="row">
            <h4><label for="message">Write a new comment for the Buyer <small>&mdash; E.g. explain what documents or information is missing.</small></label></h4>
            <textarea id="message" rows="5" v-model="message"></textarea>
          </div>
          <fieldset class="align-right">
            <WithTutorial
              name="ApplicationStatus" :order="3"
              heading="Review Bidder"
              text="See something missing? You can message your Bidder directly on their Registration Form by adding a comment."
              placement="top">
              <div style="display:block; width:1px;height:1px;"></div>
            </WithTutorial>
            <button class="btn-small btn-neutral" @click="sendMessage()" :disabled="!messageChanged"><v-icon dark :disabled="!messageChanged">message</v-icon> Send the comment</button>
          </fieldset>
        </div>

        <div class="grey-box-container box">
          <!-- <div class="clearfix row">
            <div class="col12"> -->
          <div class="clearfix row">
            <div>
              <WithTutorial
                name="ApplicationStatus" :order="4" last
                heading="Review Bidder"
                text="When you’ve made your decision, you must Accept or Decline the Bidder’s participation in your auction."
                placement="right" offset="50">
                <h3>Final Decision</h3>
              </WithTutorial>
              <div v-if="application.role === auctionRoles.APPLICANT.value">
                <div>
                  <button @click="acceptParticipantDialog()"><v-icon dark>check_circle</v-icon> Accept</button>
                  <button class="btn-neutral" @click="rejectParticipantDialog()"><v-icon dark>cancel</v-icon> Decline</button>
                </div>
                <fieldset>
                  <br />
                  <br />
                  <label>Note:</label><br/>
                  - Accept: it will authorize the buyer to participate on auction.<br />
                  - Decline: it will exclude the buyer from auction.
                </fieldset>
              </div>
              <div v-if="application.role === auctionRoles.BUYER.value">
                <div>
                  <button @click="applicantParticipantDialog()"><v-icon dark>check_circle</v-icon> Applicant</button>
                  <button class="btn-neutral" @click="rejectParticipantDialog()"><v-icon dark>cancel</v-icon> Decline</button>
                </div>
                <fieldset>
                  <br />
                  <br />
                  <label>Note:</label><br/>
                  - Applicant: it will allow the buyer to edit the application form to review and update data. It would be good if you also send a message to the buyer with instructions about what needs to be done.<br />
                  - Decline: it will exclude the buyer from auction.
                </fieldset>
              </div>
              <div v-if="application.role === auctionRoles.DISQUALIFIED.value">
                <div>
                  <button @click="acceptParticipantDialog()"><v-icon dark>check_circle</v-icon> Accept</button>
                  <button @click="applicantParticipantDialog()"><v-icon dark>check_circle</v-icon> Applicant</button>
                </div>
                <fieldset>
                  <br />
                  <br />
                  <label>Note:</label><br/>
                  - Accept: it will authorize the buyer to participate on auction.<br />
                  - Applicant: it will allow the buyer to edit the application form to review and update data. It would be good if you also send a message to the buyer with instructions about what needs to be done.
                </fieldset>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
  import { auctionRoles, agencyRelationships, agencyRelationshipLabel, auctionInvitationRoles, tenantLogoUrl, fileDocTypeLabel } from '../utils/PfxUtils.js'
  import InlineSpinner from './elements/InlineSpinner.vue'
  import FileList from './elements/FileList.vue'
  import WithTutorial from './elements/WithTutorial.vue'
  import TutorialToggle from './elements/TutorialToggle.vue'
  import tutorialMixin from '../mixins/tutorialMixin'

  export default {
    name: 'ApplicationStatusForListingAgent',
    components: {
      InlineSpinner,
      FileList,
      WithTutorial,
      TutorialToggle
    },
    mixins: [
      tutorialMixin('ApplicationStatus')
    ],
    data() {
      return {
        auctionRoles,
        agencyRelationships,
        buyerProfile: {},
        auctionId: parseInt(this.$route.params.auctionId),
        auction: {},
        applicationId: parseInt(this.$route.params.applicationId),
        application: {},
        listingAgentProfile: {},
        sellingAgentProfile: {},
        sellingAgentInvitedEmailAddress: '',
        message: '',
        buyerRequirementOptions: [],
        fileDocTypeLabel
        
      }
    },
    computed: {
      messageChanged() {
        const currentMessage = (this.application.message || '').trim()
        const newMessage = (this.message || '').trim()
        return currentMessage !== newMessage && newMessage.length > 0
      },
      areAllBuyerRequirementStatusTrue() {
        const all = this.auction.buyerRequirements.length
        const reviewed = this.application.buyerRequirementsStatus.filter(s => s.status === true).length
        return reviewed >= all
      }
    },
    created() {

      this.$PfxBackendApi.getBuyerRequirementsOptions().then(res => {
          // console.log('getBuyerRequirementsOptions res', res)
          res.data.forEach(document => {
            let item = { text: document.name, id: document.id }
            this.buyerRequirementOptions.push(item)
          })
          console.log('getBuyerRequirementsOptions Array: ', this.buyerRequirementOptions)
        }).catch(err => {
          console.error('getBuyerRequirementsOptions err', err)
        })

      this.loadData()
    },
    methods: {
      agencyRelationshipLabel,
      tenantLogoUrl,

      getLabel(id){
        return fileDocTypeLabel(id,this.buyerRequirementOptions)
      },

      loadData() {
        const that = this
        that.$store.commit('setLoadingSpinner', true)

        this.$PfxBackendApi.getAuction(that.auctionId).then(res => {
          console.log('getAuction res', res)
          that.auction = res.data || {}
          const subdomain = that.auction.tenant.subdomain

          this.$PfxBackendApi.getAuctionApplication(subdomain, that.auction.id, that.applicationId).then(res => {
            console.log('getAuctionApplication res', res)
            const application = res.data || {}

            if (application.id) {
              that.message = application.message
              that.$store.commit('setLoadingSpinner', 3)
              that.application = application

              // fetch buyer profile
              this.$PfxBackendApi.getUser(application.userId).then(res => {
                console.log('getUser (buyer) res', res)
                that.buyerProfile = res.data || {}
              }).catch(err => {
                console.error('getUser (buyer) err', err)
                this.$notify({ type: 'danger', message: 'Cannot fetch buyer profile' })
              }).then(() => that.$store.commit('setLoadingSpinner', '--'))

              // fetch selling agent
              if (application.agencyRelationship === agencyRelationships.SELLING_AGENT.value) {
                that.$store.commit('setLoadingSpinner', '++')
                this.$PfxBackendApi.getAuctionSellingAgent(subdomain, that.auction.id, that.applicationId).then(res => {
                  console.log('getAuctionSellingAgent res', res)
                  const agentRole = (res.data && res.data.sellingAgentRole) || {}
                  if (agentRole.role === auctionRoles.SELLING_AGENT.value && agentRole.userId) {
                    that.$store.commit('setLoadingSpinner', '++')
                    console.log('sellingAgentUserId [%s]', agentRole.userId)
                    this.$PfxBackendApi.getUser(agentRole.userId).then(res => {
                      console.log('getUser sellingAgentUserId res', res)
                      that.sellingAgentProfile = res.data || {}
                    }).catch(err => {
                      console.log('getUser sellingAgentUserId err', err)
                      this.$notify({ type: 'danger', message: 'Cannot fetch selling agent profile' })
                    }).then(() => that.$store.commit('setLoadingSpinner', '--'))
                  }
                }).catch(err => {
                  if (err.status === 400) {
                    // selling agent invited, but not yet responded => ignore this
                  } else {
                    console.error('getAuctionSellingAgent err', err)
                    this.$notify({type: 'danger', message: 'Cannot fetch data about selling agent'})
                  }
                }).then(() => that.$store.commit('setLoadingSpinner', '--'))
              }

              // fetch auction invitations for this auction (resolving selling agent who has not yet accepted invitation)
              this.$PfxBackendApi.getAuctionInvitations(subdomain, that.auction.id).then(res => {
                console.log('getAuctionInvitations res', res)
                const invitations = res.data || []
                const sellingAgentInvitation = invitations.reverse().find(invitation => {
                  return invitation.auctionInvitationRole === auctionInvitationRoles.SELLING_AGENT.value &&
                    invitation.auctionRole.id === that.applicationId &&
                    invitation.auctionRole.agencyRelationship === auctionRoles.SELLING_AGENT.value
                })
                console.log('getAuctionInvitations sellingAgentInvitation', sellingAgentInvitation)
                that.sellingAgentInvitedEmailAddress = sellingAgentInvitation && sellingAgentInvitation.email || null
              }).catch(err => {
                console.error('getAuctionInvitations err', err)
                this.$notify({ type: 'danger', message: 'Cannot fetch data about selling agent invitation' })
              }).then(() => that.$store.commit('setLoadingSpinner', '--'))

              // fetch listing agent profile
              const listingAgentSub = that.auction.listingAgent
              this.$PfxBackendApi.getUser(listingAgentSub).then(res => {
                console.log('getUser (listing agent) res', res)
                that.listingAgentProfile = res.data || {}
              }).catch(err => {
                console.error('getUser (listing agent) res', err)
                this.$notify({ type: 'danger', message: 'Cannot fetch listing agent profile' })
              }).then(() => that.$store.commit('setLoadingSpinner', '--'))

            } else { // no application found
              that.$store.commit('setLoadingSpinner', false)
            }

          }).catch(err => {
            console.error('getAuctionApplication err', err)
            this.$notify({ type: 'danger', message: 'Cannot fetch data about auction registration form' })
            that.$store.commit('setLoadingSpinner', false)
          })

        }).catch(err => {
          console.error('getAuction err', err)
          this.$notify({ type: 'danger', message: 'Cannot fetch data about auction' })
          that.$store.commit('setLoadingSpinner', false)
        })
      },

      // NOTE: this feature was removed (accepting individual buyer requirements)
      isBuyerRequirementStatusTrue(buyerRequirementId) {
        let response = false
        this.application.buyerRequirementsStatus.forEach(status => {
          if (buyerRequirementId === status.buyerRequirement.id) {
            response = status.status === true
          }
        })
        return response
      },

      // NOTE: this feature was removed (accepting individual buyer requirements)
      toggleBuyerRequirementStatus(buyerRequirementId) {
        if (this.auction.role.role !== auctionRoles.LISTING_AGENT.value && this.auction.role.role !== auctionRoles.DELEGATE.value && this.auction.role.role !== auctionRoles.TENANT_ADMIN.value) {
          return // e.g. buyer themselves cannot edit status, only listing agent can
        }
        if (this.application.role !== auctionRoles.APPLICANT.value) {
          return // status cannot be edited in other states than when applicant
        }
        const status = this.isBuyerRequirementStatusTrue(buyerRequirementId)
        this.setBuyerRequirementStatusTrue(buyerRequirementId, !status)
        this.$PfxBackendApi.postBuyerRequirementStatus(this.auction.tenant.subdomain, this.auctionId, this.applicationId, buyerRequirementId, !status).then(res => {
          console.log('postBuyerRequirementStatus res', res)
        }).catch(err => {
          console.error('postBuyerRequirementStatus err', err)
          this.setBuyerRequirementStatusTrue(buyerRequirementId, status) // rollback internal status
          this.$notify({ type: 'danger', message: 'Cannot update buyer requirements' })
        })
      },

      // NOTE: this feature was removed (accepting individual buyer requirements)
      setBuyerRequirementStatusTrue(buyerRequirementId, newStatus) {
        let found = false
        this.application.buyerRequirementsStatus.forEach(status => {
          if (buyerRequirementId === status.buyerRequirement.id) {
            status.status = newStatus
            found = true
          }
        })
        if (!found) {
          this.application.buyerRequirementsStatus.push({
            status: newStatus,
            buyerRequirement: {
              id: buyerRequirementId
            }
          })
        }
      },

      sendMessage() {
        if (!this.messageChanged) { return }
        const that = this
        that.$store.commit('setLoadingSpinner', true)
        this.$PfxBackendApi.postAuctionApplicationMessage(this.auction.tenant.subdomain, this.auctionId, this.applicationId, this.message).then(res => {
          console.log('postAuctionApplicationMessage res', res)
          if (res.data && res.data.message) {
            that.application.message = res.data.message
            that.$store.commit('openDialog', 'Your message was sent.')
          }
        }).catch(err => {
          console.error('postAuctionApplicationMessage error', err)
          this.$notify({ type: 'danger', message: 'Cannot send the message' })
        }).then(() => that.$store.commit('setLoadingSpinner', false))
      },

      scrollToTop() {
        document.documentElement.scrollTop = 0
        document.body.scrollTop = 0
      },

      acceptParticipantDialog() {
        this.$store.commit('openDialog', {
          text: 'You are about to accept the auction registration. ' +
          'Are you sure about accepting?',
          buttons: [
            { title: 'Cancel', class: 'btn-neutral' },
            {
              title: 'Accept registration',
              handler: () => {
                this.$store.commit('closeDialog')
                this.acceptParticipant()
              }
            }
          ]
        })
      },

      acceptParticipant() {
        const that = this
        that.$store.commit('setLoadingSpinner', true)
        this.$PfxBackendApi.postAuctionApplicationAccept(this.auction.tenant.subdomain, this.auctionId, this.applicationId).then(res => {
          console.log('postAuctionApplicationAccept res', res)
          that.loadData()
        }).catch(err => {
          console.error('postAuctionApplicationAccept error', err)
          this.$notify({ type: 'danger', message: 'Unexpected error occurred' })
        }).then(() => {
          that.$store.commit('setLoadingSpinner', false)
          this.scrollToTop()
        })
      },

      applicantParticipantDialog() {
        this.$store.commit('openDialog', {
          text: 'You are about to revert the auction registration to applicant.' +
          'Are you sure about to perform this change?',
          buttons: [
            { title: 'Cancel', class: 'btn-neutral' },
            {
              title: 'Change to applicant',
              handler: () => {
                this.$store.commit('closeDialog')
                this.applicantParticipant()
              }
            }
          ]
        })
      },

      applicantParticipant() {
        const that = this
        that.$store.commit('setLoadingSpinner', true)
        this.$PfxBackendApi.postAuctionApplicationApplicant(this.auction.tenant.subdomain, this.auctionId, this.applicationId).then(res => {
          console.log('postAuctionApplicationApplicant res', res)
          that.loadData()
        }).catch(err => {
          console.error('postAuctionApplicationApplicant error', err)
          this.$notify({ type: 'danger', message: 'Unexpected error occurred' })
        }).then(() => {
          that.$store.commit('setLoadingSpinner', false)
          this.scrollToTop()
        })
      },

      rejectParticipantDialog() {
        this.$store.commit('openDialog', {
          text: 'You are about to reject the auction registration. ' +
          'Are you sure about rejecting?',
          buttons: [
            { title: 'Cancel', class: 'btn-neutral' },
            {
              title: 'Reject registration', class: 'btn-danger',
              handler: () => {
                this.$store.commit('closeDialog')
                this.rejectParticipant()
              }
            }
          ]
        })
      },

      rejectParticipant() {
        const that = this
        that.$store.commit('setLoadingSpinner', true)
        this.$PfxBackendApi.postAuctionApplicationDecline(this.auction.tenant.subdomain, this.auctionId, this.applicationId).then(res => {
          console.log('postAuctionApplicationDecline res', res)
          that.loadData()
        }).catch(err => {
          console.error('postAuctionApplicationDecline error', err)
          this.$notify({ type: 'danger', message: 'Unexpected error occurred' })
        }).then(() => {
          that.$store.commit('setLoadingSpinner', false)
          this.scrollToTop()
        })
      }

    }
  }
</script>

<style lang="scss" scoped>
  @import '../pfx.scss';

  .clearfix { @include clearfix(); }
  .col17 { @include column(1/7); }
  .col27 { @include column(2/7); }
  .col37 { @include column(3/7); }
  .col47 { @include column(4/7); }
  .col57 { @include column(5/7); }
  .col67 { @include column(6/7); }
  .col12 { @include column(1/2, $cycle: 2); }
  .col13 { @include column(1/3); }
  .col23 { @include column(2/3); }

  .component-container {
    @include center($max-width: 720px);
  }

  .status-notification {
    .color-bar {
      text-align: center;
      color: #000;
      padding: 0.25em;
      font-size: 1.2rem;
      border-radius: 7px;
    }
    .color-bar {
      background: $color-bar-pending-bg;
      color: $color-bar-fg;
    }
    .color-bar.accepted {
      background: $color-bar-accepted-bg;
    }
    .color-bar.rejected {
      background: $color-bar-rejected-bg;
      color: $color-bar-fg-alt;
    }
    margin: 1rem 0;
  }

  .box {
    h3 {
      font-size: 1.2rem;
      margin: 0 0 1em;
      line-height: 1em;
      &:first-child { margin-top: 0; }
    }
    .row {
      margin: 0 0 1.25rem;
      h4 {
        margin: 0 0 .25rem;
        font-weight: 600;
        color: #666;
      }
    }
  }

  .user-avatar {
    height: 3rem;
    float:left;
    margin-right: .5rem;
  }

</style>



// WEBPACK FOOTER //
// src/components/ApplicationStatusForListingAgent.vue